import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';

// ─── Add Reminder Modal ──────────────────────────────────────────────────────

interface AddReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function AddReminderModal({ isOpen, onClose, onSuccess }: AddReminderModalProps) {
  const [claimSearch, setClaimSearch] = useState('');
  const [claimResults, setClaimResults] = useState<any[]>([]);
  const [selectedClaim, setSelectedClaim] = useState<any | null>(null);
  const [dueDate, setDueDate] = useState('');
  const [note, setNote] = useState('');
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ success: boolean; message: string } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Default due date to tomorrow
  useEffect(() => {
    if (isOpen && !dueDate) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setDueDate(tomorrow.toISOString().split('T')[0]);
    }
  }, [isOpen, dueDate]);

  // Debounced claim search
  useEffect(() => {
    if (!claimSearch.trim() || claimSearch.length < 2) {
      setClaimResults([]);
      return;
    }

    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const result = await api.getClaims({ search: claimSearch, limit: '8' });
        setClaimResults(result.claims || []);
      } catch {
        setClaimResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [claimSearch]);

  function resetForm() {
    setClaimSearch('');
    setClaimResults([]);
    setSelectedClaim(null);
    setDueDate('');
    setNote('');
    setSubmitResult(null);
    setSubmitError(null);
  }

  function handleClose() {
    resetForm();
    onClose();
  }

  async function handleSubmit() {
    if (!selectedClaim || !dueDate || !note.trim()) return;

    setSubmitting(true);
    setSubmitError(null);
    setSubmitResult(null);

    try {
      const result = await api.createDiaryReminder({
        claim_id: selectedClaim.claim_id.toString(),
        due_date: dueDate,
        note: note.trim(),
      });
      setSubmitResult({
        success: true,
        message: result.message || 'Reminder saved.',
      });
      setTimeout(() => {
        handleClose();
        onSuccess();
      }, 1500);
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to create reminder.');
    } finally {
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">Add Reminder</h2>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Claim search */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Claim</label>
            {selectedClaim ? (
              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <span className="text-sm font-medium text-blue-800">
                  {selectedClaim.tier_claim_num || selectedClaim.fh_claim_num}
                  <span className="text-blue-500 font-normal ml-2">{selectedClaim.handler_name}</span>
                </span>
                <button
                  onClick={() => { setSelectedClaim(null); setClaimSearch(''); }}
                  className="text-blue-400 hover:text-blue-600 text-xs font-medium"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={claimSearch}
                  onChange={(e) => setClaimSearch(e.target.value)}
                  placeholder="Search by claim number, claimant, or company..."
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {searching && (
                  <div className="absolute right-3 top-2.5">
                    <svg className="animate-spin w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                  </div>
                )}
                {/* Results dropdown */}
                {claimResults.length > 0 && !selectedClaim && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {claimResults.map((c: any) => (
                      <button
                        key={c.claim_id}
                        onClick={() => {
                          setSelectedClaim(c);
                          setClaimSearch('');
                          setClaimResults([]);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-0"
                      >
                        <span className="font-medium text-slate-800">
                          {c.tier_claim_num || c.fh_claim_num}
                        </span>
                        <span className="text-slate-500 ml-2">{c.handler_name}</span>
                        {c.company_name && (
                          <span className="text-slate-400 ml-1">| {c.company_name}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Due date */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Call defense counsel re: expert witness availability"
              rows={3}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Submit result */}
          {submitResult && (
            <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
              submitResult.success
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {submitResult.success && (
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {submitResult.message}
            </div>
          )}

          {submitError && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
              <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01" />
              </svg>
              {submitError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !selectedClaim || !dueDate || !note.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {submitting ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Saving...
              </>
            ) : (
              'Add Reminder'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface DiaryItem {
  diary_id: number;
  claim_id: string;
  fh_claim_num: string;
  tier_claim_num: string;
  diary_date: string;
  diary_type: string;
  description: string;
  completed_yn: string;
  handler_id: number;
  handler_name: string;
  company_name: string;
  total_incurred: number;
  days_relative: number;
}

interface DiaryBucket {
  count: number;
  items: DiaryItem[];
}

interface HandlerOption {
  handler_id: string;
  handler_name: string;
}

interface DiaryResponse {
  overdue: DiaryBucket;
  due_today: DiaryBucket;
  this_week: DiaryBucket;
  future: DiaryBucket;
  total: number;
  handlers: HandlerOption[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${mm}/${dd}/${yyyy}`;
}



// ─── Section Table ────────────────────────────────────────────────────────────

interface SectionTableProps {
  items: DiaryItem[];
  mode: 'overdue' | 'due_today' | 'this_week' | 'future';
}

function SectionTable({ items, mode }: SectionTableProps) {
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <p className="px-4 py-6 text-sm text-slate-400 text-center">No items</p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">TCM #</th>
            <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Diary Date</th>
            <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Type</th>
            <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Description</th>
            <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Handler</th>
            <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Company</th>
            <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">
              {mode === 'future' ? 'Due In' : 'Days Overdue'}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr
              key={item.diary_id}
              className={[
                'border-b border-slate-100 transition-colors',
                idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60',
                'hover:bg-blue-50',
              ].join(' ')}
            >
              <td className="px-4 py-3 whitespace-nowrap">
                <button
                  onClick={() => navigate(`/claims/${item.claim_id}`)}
                  className="font-mono text-blue-700 font-medium hover:underline focus:outline-none"
                >
                  {item.tier_claim_num || item.fh_claim_num || '—'}
                </button>
              </td>
              <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                {formatDate(item.diary_date)}
              </td>
              <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                {item.diary_type || '—'}
              </td>
              <td className="px-4 py-3 text-slate-700 max-w-xs truncate">
                {item.description || '—'}
              </td>
              <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                {item.handler_name || '—'}
              </td>
              <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                {item.company_name || '—'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                {mode === 'future' ? (
                  <span className="text-slate-600">
                    in {Math.abs(item.days_relative)} day{Math.abs(item.days_relative) !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span className="text-red-600 font-medium">
                    {Math.abs(item.days_relative)} day{Math.abs(item.days_relative) !== 1 ? 's' : ''}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Collapsible Section ───────────────────────────────────────────────────────

type AccentColor = 'red' | 'blue' | 'amber' | 'slate';

interface SectionCardProps {
  title: string;
  count: number;
  accent: AccentColor;
  defaultOpen: boolean;
  items: DiaryItem[];
  mode: 'overdue' | 'due_today' | 'this_week' | 'future';
}

const accentStyles: Record<AccentColor, { border: string; badge: string; chevron: string }> = {
  red: {
    border: 'border-l-4 border-l-red-500',
    badge: 'bg-red-100 text-red-700',
    chevron: 'text-red-400',
  },
  blue: {
    border: 'border-l-4 border-l-blue-500',
    badge: 'bg-blue-100 text-blue-700',
    chevron: 'text-blue-400',
  },
  amber: {
    border: 'border-l-4 border-l-amber-500',
    badge: 'bg-amber-100 text-amber-700',
    chevron: 'text-amber-400',
  },
  slate: {
    border: 'border-l-4 border-l-slate-400',
    badge: 'bg-slate-100 text-slate-600',
    chevron: 'text-slate-400',
  },
};

function SectionCard({ title, count, accent, defaultOpen, items, mode }: SectionCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  const styles = accentStyles[accent];

  return (
    <div className={`bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden ${styles.border}`}>
      {/* Header */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors focus:outline-none"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <span className="text-base font-semibold text-slate-800">{title}</span>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold tabular-nums ${styles.badge}`}>
            {count}
          </span>
        </div>
        <svg
          className={`w-5 h-5 transition-transform duration-200 ${styles.chevron} ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Body */}
      {open && (
        <div className="border-t border-slate-100">
          <SectionTable items={items} mode={mode} />
        </div>
      )}
    </div>
  );
}

// ─── DiaryView ────────────────────────────────────────────────────────────────

export default function DiaryView() {
  const [handlerFilter, setHandlerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');

  const [data, setData] = useState<DiaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [reminderOpen, setReminderOpen] = useState(false);

  const fetchDiary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (handlerFilter) params.handler_id = handlerFilter;
      if (statusFilter) params.status = statusFilter;

      const result: DiaryResponse = await api.getDiary(params);
      setData(result);
    } catch (err: any) {
      setError(err?.message || 'Failed to load diary items.');
    } finally {
      setLoading(false);
    }
  }, [handlerFilter, statusFilter]);

  useEffect(() => {
    fetchDiary();
  }, [fetchDiary]);

  const handlers = data?.handlers ?? [];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Page title */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Diary View</h1>
            <p className="text-sm text-slate-500 mt-0.5">Task Management</p>
          </div>
          <button
            onClick={() => setReminderOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            + Add Reminder
          </button>
        </div>

        {/* Filter bar */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            {/* Handler dropdown */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600 whitespace-nowrap">Handler:</label>
              <select
                value={handlerFilter}
                onChange={(e) => setHandlerFilter(e.target.value)}
                className="py-2 px-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 text-slate-700"
              >
                <option value="">All Handlers</option>
                {handlers.map((h) => (
                  <option key={h.handler_id} value={h.handler_id}>{h.handler_name}</option>
                ))}
              </select>
            </div>

            {/* Status toggle */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600 whitespace-nowrap">Status:</label>
              <div className="flex rounded-lg border border-slate-300 overflow-hidden">
                <button
                  onClick={() => setStatusFilter('open')}
                  className={[
                    'px-4 py-2 text-sm font-medium transition-colors',
                    statusFilter === 'open'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50',
                  ].join(' ')}
                >
                  Open
                </button>
                <button
                  onClick={() => setStatusFilter('')}
                  className={[
                    'px-4 py-2 text-sm font-medium transition-colors border-l border-slate-300',
                    statusFilter === ''
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50',
                  ].join(' ')}
                >
                  All
                </button>
              </div>
            </div>

            {/* Total count indicator */}
            {data && (
              <span className="ml-auto text-sm text-slate-500 whitespace-nowrap">
                {data.total.toLocaleString()} total item{data.total !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Main content */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl border border-red-200 shadow-sm px-6 py-10 flex flex-col items-center gap-4">
            <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p className="text-slate-700 font-medium">{error}</p>
            <button
              onClick={fetchDiary}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        ) : data ? (
          <>
            {/* Overdue */}
            {data.overdue.count > 0 && (
              <SectionCard
                title="Overdue"
                count={data.overdue.count}
                accent="red"
                defaultOpen={true}
                items={data.overdue.items}
                mode="overdue"
              />
            )}

            {/* Due Today */}
            <SectionCard
              title="Due Today"
              count={data.due_today.count}
              accent="blue"
              defaultOpen={true}
              items={data.due_today.items}
              mode="due_today"
            />

            {/* This Week */}
            <SectionCard
              title="This Week"
              count={data.this_week.count}
              accent="amber"
              defaultOpen={data.this_week.count <= 10}
              items={data.this_week.items}
              mode="this_week"
            />

            {/* Upcoming */}
            <SectionCard
              title="Upcoming"
              count={data.future.count}
              accent="slate"
              defaultOpen={false}
              items={data.future.items}
              mode="future"
            />
          </>
        ) : null}
      </div>

      {/* Add Reminder Modal */}
      <AddReminderModal
        isOpen={reminderOpen}
        onClose={() => setReminderOpen(false)}
        onSuccess={() => fetchDiary()}
      />
    </div>
  );
}
